/**
 * Helper functions for filter logic
 */
import {
  SORT_ORDER_TITLE_ASC_INT,
  SORT_ORDER_TITLE_DESC_INT,
  SORT_ORDER_DATE_DESC_INT,
  SORT_ORDER_RATING_DESC_INT,
  SORT_ORDER_NEWS_DESC_INT,
  SORT_ORDER_TYPE_ASC_INT,
  DEFAULT_FILTER_OBJECT,
  STORAGE_KEY_SORT_ORDER,
} from "../../constants";

/**
 * Checks if any category value is active
 * @param {Object} categoriesValue - Object containing category filter values
 * @returns {boolean} True if any category is active
 */
export const hasAnyActiveCategory = (categoriesValue) => {
  if (!categoriesValue) {
    return false;
  }
  return Object.values(categoriesValue).some((value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== 0 && value !== null && value !== undefined,
  );
};

/**
 * Checks if a session filter is active
 * @param {string|Array} sessionValue - Session region or city value
 * @returns {boolean} True if session filter is active
 */
export const isSessionFilterActive = (sessionValue) => {
  if (!sessionValue) {
    return false;
  }
  return Array.isArray(sessionValue) ? sessionValue.length > 0 : !!sessionValue;
};

/**
 * Checks if there are any active filters
 * @param {Object} filters - Filter object
 * @returns {boolean} True if any filter is active
 */
export const hasActiveFilters = (filters = {}) => {
  const categoriesValue = filters.categoriesValue || {};
  const anyCategory = hasAnyActiveCategory(categoriesValue);
  const regionActive = isSessionFilterActive(filters.sessionRegion);
  const cityActive = isSessionFilterActive(filters.sessionCity);

  return (
    (filters.searchText && filters.searchText.trim().length > 0) ||
    !!filters.startDate ||
    !!filters.endDate ||
    regionActive ||
    cityActive ||
    anyCategory
  );
};

/**
 * Extracts category values from filters object with defaults
 * @param {Object} categoriesValue - Category values object
 * @returns {Object} Object with category1-5 values, defaulting to 0
 */
export const extractCategoryValues = (categoriesValue = {}) => {
  return {
    category1: categoriesValue.category1 || 0,
    category2: categoriesValue.category2 || 0,
    category3: categoriesValue.category3 || 0,
    category4: categoriesValue.category4 || 0,
    category5: categoriesValue.category5 || 0,
  };
};

/**
 * Builds options object for fetchCatalogueData from filter state
 * @param {Object} filters - Filter object
 * @param {number} pageNum - Page number
 * @returns {import('./types/fetchOptions').FetchCatalogueDataOptions} Options object for fetchCatalogueData
 */
export const buildFetchParams = (filters = {}, pageNum = 1) => {
  const categories = extractCategoryValues(filters.categoriesValue);
  return {
    searchInput: filters.searchText || "",
    category1: categories.category1,
    category2: categories.category2,
    category3: categories.category3,
    category4: categories.category4,
    category5: categories.category5,
    pageNum,
    sortOrderParam: filters.sortOrder || 0,
    startDate: filters.startDate || null,
    endDate: filters.endDate || null,
    sessionRegion: filters.sessionRegion || "",
    sessionCity: filters.sessionCity || "",
  };
};

/**
 * Checks if updates should trigger a refetch
 * @param {Object} updates - Object containing filter updates
 * @returns {boolean} True if refetch is needed
 */
export const shouldRefetchOnUpdate = (updates) => {
  const keys = Object.keys(updates);
  return keys.some((k) =>
    ["startDate", "endDate", "sessionRegion", "sessionCity"].includes(k),
  );
};

/**
 * Converts sort order parameter to sort type string
 * @param {number|string} sortOrderParam - Sort order parameter
 * @returns {string} Sort type string
 */
export const getSortTypeFromParam = (sortOrderParam) => {
  const param = parseInt(sortOrderParam, 10);

  // Default fallback mapping
  switch (param) {
    case SORT_ORDER_TITLE_ASC_INT:
      return "title";
    case SORT_ORDER_TITLE_DESC_INT:
      return "title_desc";
    case SORT_ORDER_DATE_DESC_INT:
      return "date";
    case SORT_ORDER_RATING_DESC_INT:
      return "rating";
    case SORT_ORDER_NEWS_DESC_INT:
      return "news";
    case SORT_ORDER_TYPE_ASC_INT:
      return "type";
    default:
      return "title";
  }
};

/**
 * Applies filters to a list of courses
 * @param {Array} courses - Array of courses to filter
 * @param {Object} filters - Filter object
 * @param {Object} filterService - Filter service instance
 * @param {Object} sortService - Sort service instance
 * @returns {Array} Filtered and sorted courses
 */
export const applyFiltersToCourses = (
  courses,
  filters,
  filterService,
  sortService,
) => {
  if (!courses) {
    return [];
  }

  const {
    searchText,
    categoriesValue,
    startDate,
    endDate,
    sessionRegion,
    sessionCity,
    sortOrder,
  } = filters || {};

  let filteredCourses = courses;

  // Apply search filter
  if (searchText) {
    filteredCourses = filterService.filterBySearchText(
      filteredCourses,
      searchText,
    );
  }

  // Apply category filters
  if (categoriesValue) {
    filteredCourses = filterService.filterByCategories(
      filteredCourses,
      categoriesValue,
    );
  }

  // Apply date filters
  if (startDate) {
    filteredCourses = filterService.filterByStartDate(
      filteredCourses,
      startDate,
    );
  }

  if (endDate) {
    filteredCourses = filterService.filterByEndDate(filteredCourses, endDate);
  }

  // Apply session filters
  if (sessionRegion) {
    filteredCourses = filterService.filterBySessionRegion(
      filteredCourses,
      sessionRegion,
    );
  }

  if (sessionCity) {
    filteredCourses = filterService.filterBySessionCity(
      filteredCourses,
      sessionCity,
    );
  }

  // Apply sorting
  if (sortOrder) {
    const sortType = getSortTypeFromParam(sortOrder);
    filteredCourses = sortService.sort(filteredCourses, sortType);
  }

  return filteredCourses;
};

/**
 * Creates default filter object
 * @returns {Object} Default filter object
 */
export const createDefaultFilters = () => {
  return {
    searchText: "",
    categoriesValue: DEFAULT_FILTER_OBJECT,
    startDate: "",
    endDate: "",
    sessionRegion: "",
    sessionCity: "",
  };
};

/**
 * Gets saved sort order from localStorage
 * @returns {number} Saved sort order or 0 if not found
 */
export const getSavedSortOrder = () => {
  const savedSort = parseInt(
    typeof window !== "undefined"
      ? window.localStorage.getItem(STORAGE_KEY_SORT_ORDER)
      : 0,
    10,
  );
  return Number.isNaN(savedSort) ? 0 : savedSort;
};

/**
 * Saves sort order to localStorage
 * @param {number} sortOrder - Sort order to save
 */
export const saveSortOrder = (sortOrder) => {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        STORAGE_KEY_SORT_ORDER,
        String(sortOrder ?? 0),
      );
    }
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Resets sort order in localStorage
 */
export const resetSortOrderStorage = () => {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY_SORT_ORDER, "0");
    }
  } catch {
    // Ignore localStorage errors
  }
};
